import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { useMe } from '../auth/context';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { addDays, appointmentStatusLabels, fmtDate, fmtTime, fromLocal, localDate } from './format';
import { listDue, taskNote } from './nextCall';

// Makler-Startseite (OFFENE_FRAGEN B-28): Was kommt als Nächstes? Eigene Termine der nächsten zwei Wochen und eigene
// Rückrufe/Wiedervorlagen – kurz, mit Weg zur Wochenansicht. Nur das, woran der Makler selbst arbeitet.

const DAYS = 14;
const SHOWN = 8;

export function MaklerUpcoming() {
  const { project } = useProject();
  const papi = useProjectApi();
  const me = useMe();
  const base = `/p/${project.key}`;
  const tomorrow = addDays(localDate(), 1);
  const q = { from: fromLocal(tomorrow, '00:00'), to: fromLocal(addDays(tomorrow, DAYS), '00:00'), user_id: me.user.id };
  const list = useQuery({ queryKey: projectKeys.calendar(project.id, q), queryFn: ({ signal }) => papi.calendar(q, signal), refetchInterval: 300_000 });
  const items = (list.data?.items ?? []).filter((a) => a.status !== 'CANCELED').sort((a, b) => a.start_at.localeCompare(b.start_at));
  return (
    <section className="card" aria-labelledby="upcoming-title">
      <div className="section-head">
        <h2 id="upcoming-title">
          Nächste Termine {list.data && <span className="badge badge-muted">{items.length}</span>}
        </h2>
        <Link to={`${base}/termine?ansicht=kalender&view=week`}>Woche & Monat →</Link>
      </div>
      {list.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <p className="muted">In den nächsten zwei Wochen sind keine weiteren Termine eingeplant.</p>
      ) : (
        <ul className="today-list">
          {items.slice(0, SHOWN).map((a) => (
            <li key={a.id} className="today-item">
              <span className="today-time">
                {fmtDate(a.start_at)} {fmtTime(a.start_at)}
              </span>
              <span className="today-lead">
                <Link to={`${base}/termine/${a.id}`}>{a.lead_name ?? a.title}</Link>
                {a.city ? ` · ${a.city}` : ''}
                {a.type_name && <small>{a.type_name}</small>}
              </span>
              {a.status !== 'PLANNED' && <span className="badge badge-muted">{appointmentStatusLabels[a.status]}</span>}
            </li>
          ))}
        </ul>
      )}
      {items.length > SHOWN && (
        <p className="today-more">
          <Link to={`${base}/termine?ansicht=kalender&view=month`}>Alle {items.length} Termine →</Link>
        </p>
      )}
    </section>
  );
}

export function MaklerFollowUps() {
  const { project } = useProject();
  const papi = useProjectApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const base = `/p/${project.key}`;
  // Nur mir zugewiesene (nicht die unverteilten des ganzen Projekts).
  const q = { assigned: me.user.id, status: 'open' as const, due: 'all' as const };
  const list = useQuery({ queryKey: projectKeys.followUps(project.id, q), queryFn: ({ signal }) => papi.followUps(q, signal), refetchInterval: 120_000 });
  const complete = useMutation({
    mutationFn: (id: string) => papi.completeFollowUp(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'followups'] }),
  });
  const items = [...(list.data?.items ?? [])].sort((a, b) => a.due_at.localeCompare(b.due_at));
  if (list.isSuccess && items.length === 0) return null;
  return (
    <section className="card" aria-labelledby="my-followups-title">
      <div className="section-head">
        <h2 id="my-followups-title">
          Meine Rückrufe & Wiedervorlagen {list.data && <span className="badge badge-muted">{items.length}</span>}
        </h2>
        {items.some((i) => i.overdue) && <span className="badge badge-error">{items.filter((i) => i.overdue).length} überfällig</span>}
      </div>
      {list.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <ul className="today-list">
          {items.slice(0, SHOWN).map((f) => (
            <li key={f.id} className={f.overdue ? 'today-item overdue' : 'today-item'}>
              <span className="today-time">{listDue(f.due_at)}</span>
              <span className={f.type === 'CALLBACK' ? 'badge badge-warn' : 'badge badge-muted'}>{f.type === 'CALLBACK' ? 'Rückruf' : 'Wiedervorlage'}</span>
              <span className="today-lead">
                <Link to={`${base}/leads/${f.lead_id}`}>{f.lead_name}</Link>
                {f.lead_city ? ` · ${f.lead_city}` : ''}
                {(f.reason || taskNote(f.title)) && <small>{f.reason || taskNote(f.title)}</small>}
              </span>
              <span className="today-actions">
                <button type="button" className="btn btn-small" disabled={complete.isPending} onClick={() => complete.mutate(f.id)}>
                  Erledigt
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {complete.isError && <MutationError error={complete.error} />}
    </section>
  );
}
